/**
 * PD-17109 — the companies on a project and the competitors on a project or a
 * job (sales relationships plan, section 4; owner decisions 2026-10-16).
 *
 * The ROLES are a fixed set: they say what kind of company it is on the
 * building, and reports and the Sales Helper group by them. The STATUSES are
 * tenant-editable catalog lists (catalog_value, types
 * `project_participant_status` and `competitor_engagement_status`); every
 * status value maps to one of the small fixed OUTCOME sets below, the way an
 * opportunity stage maps to open / won / lost, so later actions key off the
 * outcome and never off a label a tenant may rename.
 */

/** What a company does on the project. */
export const PROJECT_PARTICIPANT_ROLES = [
	'general_contractor',
	'mechanical',
	'plumbing',
	'electrical',
	'fire_protection',
	'engineer_architect',
	'owner',
	'other',
] as const;
export type ProjectParticipantRole = (typeof PROJECT_PARTICIPANT_ROLES)[number];

export const PROJECT_PARTICIPANT_ROLE_LABELS: Readonly<Record<ProjectParticipantRole, string>> = {
	general_contractor: 'General Contractor',
	mechanical: 'Mechanical',
	plumbing: 'Plumbing',
	electrical: 'Electrical',
	fire_protection: 'Fire Protection',
	engineer_architect: 'Engineer or Architect',
	owner: 'Owner',
	other: 'Other',
};

/**
 * Where a company stands on the project, whatever the tenant calls it:
 * - bidding: pricing the work, not decided;
 * - awarded: has the contract, work not started;
 * - active: doing the work;
 * - lost: another company got the contract;
 * - withdrawn: stopped bidding, or left the project.
 */
export const PROJECT_PARTICIPANT_STATUS_OUTCOMES = [
	'bidding',
	'awarded',
	'active',
	'lost',
	'withdrawn',
] as const;
export type ProjectParticipantStatusOutcome = (typeof PROJECT_PARTICIPANT_STATUS_OUTCOMES)[number];

export const PROJECT_PARTICIPANT_STATUS_OUTCOME_LABELS: Readonly<Record<ProjectParticipantStatusOutcome, string>> = {
	bidding: 'Bidding',
	awarded: 'Awarded',
	active: 'Active',
	lost: 'Lost',
	withdrawn: 'Withdrawn',
};

/**
 * Where a competitor stands on a project or a job, whatever the tenant calls
 * it:
 * - quoting: pricing it against us, not decided;
 * - supplying: delivering material on it;
 * - won: got the business (on a project, the part they quoted);
 * - lost: did not get it.
 */
export const COMPETITOR_ENGAGEMENT_STATUS_OUTCOMES = ['quoting', 'supplying', 'won', 'lost'] as const;
export type CompetitorEngagementStatusOutcome = (typeof COMPETITOR_ENGAGEMENT_STATUS_OUTCOMES)[number];

export const COMPETITOR_ENGAGEMENT_STATUS_OUTCOME_LABELS: Readonly<Record<CompetitorEngagementStatusOutcome, string>> = {
	quoting: 'Quoting',
	supplying: 'Supplying',
	won: 'Won',
	lost: 'Lost',
};

/** A tenant's starting status list: each value's name and the outcome it means. */
export interface DefaultStatusValueModel {
	name: string;
	outcome: string;
}

/** Seeded per tenant; admins rename, reorder or add in Settings → Catalog Values. */
export const DEFAULT_PROJECT_PARTICIPANT_STATUSES: ReadonlyArray<DefaultStatusValueModel> = [
	{ name: 'Bidding', outcome: 'bidding' },
	{ name: 'Awarded', outcome: 'awarded' },
	{ name: 'Working', outcome: 'active' },
	{ name: 'Lost', outcome: 'lost' },
	{ name: 'Withdrawn', outcome: 'withdrawn' },
];

/** One list for project and job competitors (owner decision 2026-10-16: the same defaults). */
export const DEFAULT_COMPETITOR_ENGAGEMENT_STATUSES: ReadonlyArray<DefaultStatusValueModel> = [
	{ name: 'Quoting', outcome: 'quoting' },
	{ name: 'Supplying', outcome: 'supplying' },
	{ name: 'Won', outcome: 'won' },
	{ name: 'Lost', outcome: 'lost' },
];

/** A role as said ("GC", "mechanical contractor", "Engineer"), or null when it is not one. */
export function readProjectParticipantRole(value: unknown): ProjectParticipantRole | null {
	if (typeof value !== 'string') {
		return null;
	}

	const key = value
		.trim()
		.toLowerCase()
		.replace(/&/g, ' and ')
		.replace(/[^a-z\s_-]+/g, ' ')
		.replace(/\b(contractor|sub|subcontractor|trade)\b/g, ' ')
		.trim()
		.replace(/[\s-]+/g, '_')
		.replace(/^_+|_+$/g, '');

	if ((PROJECT_PARTICIPANT_ROLES as ReadonlyArray<string>).includes(key)) {
		return key as ProjectParticipantRole;
	}

	const aliases: Readonly<Record<string, ProjectParticipantRole>> = {
		gc: 'general_contractor',
		general: 'general_contractor',
		cm: 'general_contractor',
		construction_manager: 'general_contractor',
		mech: 'mechanical',
		hvac: 'mechanical',
		plumber: 'plumbing',
		plumbing_and_mechanical: 'mechanical',
		electric: 'electrical',
		electrician: 'electrical',
		fire: 'fire_protection',
		sprinkler: 'fire_protection',
		sprinklers: 'fire_protection',
		fire_sprinkler: 'fire_protection',
		engineer: 'engineer_architect',
		architect: 'engineer_architect',
		engineer_or_architect: 'engineer_architect',
		engineer_and_architect: 'engineer_architect',
		specifier: 'engineer_architect',
		building_owner: 'owner',
		developer: 'owner',
	};

	return Object.prototype.hasOwnProperty.call(aliases, key) ? (aliases[key] ?? null) : null;
}
